import type { ReactNode } from 'react';
import { Select } from 'antd';
import { ChevronLeftIcon, ChevronRightIcon } from '../assets/icons';

interface PaginationProps {
  /** 1-based index of the first row shown. */
  rangeStart: number;
  /** 1-based index of the last row shown. */
  rangeEnd: number;
  total: number;
  /** Optional trailing noun, e.g. "users", "roles". */
  noun?: string;
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Current page size — renders the "Rows per page" control when paired with onPageSizeChange. */
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Optional extra control on the right. */
  trailing?: ReactNode;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

/** Shared list-page pagination footer (Daily Directions design). */
export default function Pagination({
  rangeStart,
  rangeEnd,
  total,
  noun,
  page,
  totalPages,
  onChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  trailing,
}: PaginationProps) {
  const current = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
  const pages: (number | 'ellipsis')[] =
    totalPages <= 4
      ? Array.from({ length: totalPages }, (_, i) => i + 1)
      : [1, 2, 3, 'ellipsis', totalPages];

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#f1f5f9] bg-[#f8fafc] px-6 py-4">
      <span className="text-sm font-medium text-[#64748b]">
        Showing{' '}
        <span className="font-semibold text-ink">
          {rangeStart}-{rangeEnd}
        </span>{' '}
        of <span className="font-semibold text-ink">{total}</span>
        {noun ? ` ${noun}` : ''}
      </span>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            disabled={current <= 1}
            onClick={() => onChange(current - 1)}
            className="flex size-8 items-center justify-center rounded text-ink-secondary transition-colors hover:bg-brand-shade disabled:opacity-40"
          >
            <ChevronLeftIcon className="h-[10px] w-[6px]" />
          </button>

          {pages.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="px-1 text-sm text-[#94a3b8]">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onChange(p)}
                className={[
                  'flex size-8 items-center justify-center rounded text-sm font-medium transition-colors',
                  p === current ? 'bg-brand text-white' : 'text-[#475569] hover:bg-brand-shade',
                ].join(' ')}
              >
                {p}
              </button>
            ),
          )}

          <button
            type="button"
            aria-label="Next page"
            disabled={current >= totalPages}
            onClick={() => onChange(current + 1)}
            className="flex size-8 items-center justify-center rounded text-ink-secondary transition-colors hover:bg-brand-shade disabled:opacity-40"
          >
            <ChevronRightIcon className="h-[10px] w-[6px]" />
          </button>
        </div>

        {onPageSizeChange && pageSize != null && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#64748b]">
              Rows per page:
            </span>
            <Select
              size="small"
              variant="borderless"
              value={pageSize}
              onChange={onPageSizeChange}
              options={pageSizeOptions.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
        )}

        {trailing}
      </div>
    </div>
  );
}
